/**
 * Page-numbering helpers for policy documents.
 *
 * The page model is positional: when cover + TOC are present they occupy the
 * leading slots (cover = 0, TOC = 1) and content starts after them. When the
 * cover/TOC have been removed there is no leading chrome — content starts at
 * index 0. There is no `pageType` field, so every render surface (editor,
 * PDF, HTML, DOCX) derives the cover/TOC prefix length from the page list
 * itself via `getCoverTocPageCount` and passes it to these helpers. These
 * helpers are the single source of truth for "which pages get a number, and
 * what does it say" so all surfaces stay in sync.
 *
 * Page numbers are render-only chrome — they are NEVER written into `plateValue`
 * (which re-serializes to markdown on save).
 */

/** Default cover + TOC prefix length when both leading chrome pages exist. */
export const CONTENT_PAGE_START_INDEX = 2;

/**
 * Detect how many leading cover/TOC pages a page list actually contains.
 * The TOC page is reliably named "Table of Contents"; the cover page takes
 * the policy title as its name (so it is not detectable by name). Because
 * cover + TOC are always added/removed as a pair, the TOC's position tells
 * us the prefix length: TOC at index 1 ⇒ cover + TOC (2); TOC at index 0 ⇒
 * TOC only (1); no TOC ⇒ no chrome (0).
 */
export const getCoverTocPageCount = (pages: ReadonlyArray<{ name?: string }>): number => {
  if (pages.length > 1 && pages[1]?.name === "Table of Contents") return 2;
  if (pages.length > 0 && pages[0]?.name === "Table of Contents") return 1;
  return 0;
};

/** Number of content pages (total pages minus the cover/TOC prefix), clamped at 0. */
export const getContentPageCount = (
  totalPages: number,
  coverTocCount: number = CONTENT_PAGE_START_INDEX,
): number => Math.max(0, totalPages - coverTocCount);

/**
 * The 1-based content page number for a given page index, or null for the
 * cover/TOC pages (which get no number).
 */
export const getContentPageNumber = (
  index: number,
  coverTocCount: number = CONTENT_PAGE_START_INDEX,
): number | null =>
  index < coverTocCount ? null : index - coverTocCount + 1;

/**
 * The display label ("N of M") for a page index, or null for cover/TOC.
 * M = number of content pages (cover/TOC prefix excluded).
 */
export const formatPageNumberLabel = (
  index: number,
  totalPages: number,
  coverTocCount: number = CONTENT_PAGE_START_INDEX,
): string | null => {
  const n = getContentPageNumber(index, coverTocCount);
  if (n === null) return null;
  return `${n} of ${getContentPageCount(totalPages, coverTocCount)}`;
};